import { useState, useMemo, useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js';
import { useDashboard } from '../../hooks/useDashboard';
import { useCache } from '../../hooks/useCache';
import { fetchTableOccupancy, mediaUrl } from '../../api';
import ImageLightbox from '../ui/ImageLightbox';
import { ChartSkeleton } from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import styles from './TableMonitoring.module.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler, Legend);

/* ---------- helpers ---------- */

function resolveImageUrl(url) {
  return mediaUrl(url);
}

function hourBadge(hourRange) {
  if (!hourRange) return '—';
  const [start, end] = hourRange.split('-');
  const fmt = part => {
    const h = parseInt(part, 10);
    if (!Number.isFinite(h)) return part;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hh = (h % 12 || 12).toString().padStart(2, '0');
    return `${hh}${ampm}`;
  };
  if (!end) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
}

function fmtPct(n, d = 0) {
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(d)}%`;
}
function fmtNum(n) {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString();
}
function fmtMin(n, d = 0) {
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(d)} min`;
}

function groupByStream(tables) {
  // Group tables by a stream prefix derived from zone name (e.g. "A-1" → stream "A").
  const streamsMap = new Map();
  tables.forEach((t, idx) => {
    const zone = t.zone || t.name || `T${idx}`;
    const m = zone.match(/^([A-Za-z]+)/);
    const key = m ? m[1].toUpperCase() : 'A';
    if (!streamsMap.has(key)) streamsMap.set(key, []);
    streamsMap.get(key).push({ table: t, globalLabel: t.name || zone });
  });
  const streams = [...streamsMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, tbls], i) => ({ index: i + 1, key, tables: tbls }));
  const labelFor = zone => {
    const t = tables.find(x => x.zone === zone || x.name === zone);
    return t?.name || zone;
  };
  return { streams, labelFor };
}

function heatTier(t) {
  if (t >= 0.66) return 'green';
  if (t >= 0.33) return 'amber';
  return 'red';
}

/* ---------- OverallCard ---------- */

function buildOccupancyStats(tables, hours) {
  let occSum = 0, occN = 0;
  let totalPersonMin = 0;
  let totalSeatMinAvailable = 0;
  let totalPersonVisits = 0;
  const perHour = new Map();
  tables.forEach(t => {
    const seats = t.seatCount || 4;
    hours.forEach(h => {
      const hd = t.hourly?.[h];
      if (!hd) return;
      if (hd.occupancy != null) {
        occSum += hd.occupancy;
        occN += 1;
        const cur = perHour.get(h) || { sum: 0, n: 0 };
        cur.sum += hd.occupancy; cur.n += 1;
        perHour.set(h, cur);
      }
      totalPersonMin += hd.personMinutes || 0;
      totalSeatMinAvailable += seats * 60;
      (hd.turnDetails || []).forEach(td => { totalPersonVisits += td.groupSize || 0; });
    });
  });
  const avg = occN ? occSum / occN : 0;
  const avgSeatOcc = totalSeatMinAvailable ? (totalPersonMin / totalSeatMinAvailable) * 100 : 0;
  const avgDwellPerPerson = totalPersonVisits ? totalPersonMin / totalPersonVisits : 0;
  let peakHour = '—', peakVal = -1;
  perHour.forEach((v, k) => {
    const a = v.n ? v.sum / v.n : 0;
    if (a > peakVal) { peakVal = a; peakHour = hourBadge(k); }
  });
  return [
    { label: 'Active Tables', value: fmtNum(tables.length), sub: 'monitored by camera pipeline', accent: 'blue' },
    { label: 'Avg Table Occupancy', value: fmtPct(avg), sub: 'across day · per table', accent: 'green' },
    { label: 'Avg Seat Occupancy', value: fmtPct(avgSeatOcc), sub: 'seats in use on avg', accent: 'purple' },
    { label: 'Peak Occupancy Hour', value: peakHour, sub: `${fmtPct(peakVal > 0 ? peakVal : 0)} occupancy`, accent: 'amber' },
    { label: 'Avg Dwell per Person', value: `${Math.round(avgDwellPerPerson)} min`, sub: 'time each person spent', accent: 'blue' },
  ];
}

function buildTurnoverStats(tables, hours) {
  let totalTurns = 0;
  let dwSum = 0, dwDen = 0;
  let gsSum = 0, gsDen = 0;
  const perHourTurns = new Map();
  tables.forEach(t => {
    hours.forEach(h => {
      const hd = t.hourly?.[h];
      if (!hd) return;
      const turns = hd.turns || 0;
      totalTurns += turns;
      if (turns > 0) perHourTurns.set(h, (perHourTurns.get(h) || 0) + turns);
      (hd.turnDetails || []).forEach(td => {
        if (td.duration > 0) { dwSum += td.duration; dwDen += 1; }
        if (td.groupSize > 0) { gsSum += td.groupSize; gsDen += 1; }
      });
    });
  });
  let peakHour = '—', peakTurns = -1;
  perHourTurns.forEach((v, k) => {
    if (v > peakTurns) { peakTurns = v; peakHour = hourBadge(k); }
  });
  return [
    { label: 'Total Turns', value: fmtNum(totalTurns), sub: `${tables.length} tables · whole day`, accent: 'blue' },
    { label: 'Avg Turns / Table', value: Math.round(tables.length ? totalTurns / tables.length : 0), sub: 'parties served per table', accent: 'green' },
    { label: 'Avg Turn Duration', value: fmtMin(dwDen ? dwSum / dwDen : 0), sub: 'across all turns', accent: 'amber' },
    { label: 'Avg Group Size', value: fmtNum(Math.round(gsDen ? gsSum / gsDen : 0)), sub: 'guests per seating', accent: 'purple' },
    { label: 'Peak Turnover Hour', value: peakHour, sub: `${peakTurns > 0 ? peakTurns : 0} turns`, accent: 'gray' },
  ];
}

function OverallCard({ tables, hours, metric }) {
  const stats = useMemo(() => {
    if (!tables.length) return [];
    return metric === 'occupancy' ? buildOccupancyStats(tables, hours) : buildTurnoverStats(tables, hours);
  }, [tables, hours, metric]);

  return (
    <div className={styles.overallCard}>
      <div className={styles.cardHdr}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className={styles.overallIcon}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div>
            <div className={styles.cardTitle}>
              {metric === 'occupancy' ? 'Occupancy Overview' : 'Turnover Overview'}
            </div>
            <div className={styles.cardSub}>Whole-day aggregate across every table and hour</div>
          </div>
        </div>
      </div>
      <div
        className={styles.statGrid}
        style={{ '--stat-cols': Math.max(stats.length, 1) }}
      >
        {stats.length === 0 ? (
          <div className={styles.statEmpty}>No data.</div>
        ) : stats.map(s => (
          <div key={s.label} className={`${styles.statCell} ${styles[`accent_${s.accent}`]}`}>
            <div className={styles.statLabel}>{s.label}</div>
            <div className={styles.statValue}>{s.value}</div>
            {s.sub && <div className={styles.statSub}>{s.sub}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- TablesList ---------- */

function TablesList({ streams, hours, metric, selectedZone, onSelect }) {
  const ranked = useMemo(() => {
    const flat = [];
    for (const s of streams) {
      for (const { table, globalLabel } of s.tables) {
        let occSum = 0, occN = 0, turns = 0, people = 0;
        hours.forEach(h => {
          const hd = table.hourly?.[h];
          if (!hd) return;
          if (hd.occupancy != null) { occSum += hd.occupancy; occN += 1; }
          turns += hd.turns || 0;
          (hd.turnDetails || []).forEach(td => { people += td.groupSize || 0; });
        });
        flat.push({
          zone: table.zone || globalLabel,
          label: globalLabel,
          streamIndex: s.index,
          avgOccupancy: occN ? occSum / occN : 0,
          totalTurns: turns,
          totalPeople: people,
        });
      }
    }
    const pick = f => metric === 'occupancy' ? f.avgOccupancy : f.totalTurns;
    const primaries = flat.map(pick);
    const maxP = Math.max(1, ...primaries);
    const minP = primaries.length ? Math.min(...primaries) : 0;
    const range = Math.max(0.0001, maxP - minP);
    const sorted = [...flat].sort((a, b) => pick(b) - pick(a));
    const list = sorted.map((f, i) => ({
      ...f,
      primary: pick(f),
      rank: i + 1,
      intensity: (pick(f) - minP) / range,
    }));
    return { list, maxP, minP };
  }, [streams, hours, metric]);

  const isOcc = metric === 'occupancy';
  const unit = isOcc ? '' : 'turns';
  const title = isOcc ? 'Tables by Occupancy' : 'Tables by Turns';
  const sub = isOcc
    ? 'Ranked by avg occupancy · click to view captures'
    : 'Ranked busiest → least busy · click to view captures';

  return (
    <div className={styles.listCard}>
      <div className={styles.cardHdr}>
        <div>
          <div className={styles.cardTitle}>{title}</div>
          <div className={styles.cardSub}>{sub}</div>
        </div>
        <div className={styles.countBadge}>{ranked.list.length} tables</div>
      </div>

      <div className={styles.legendRow}>
        <span className={styles.legendDot} style={{ background: 'var(--green)' }} /> Busiest
        <span className={styles.legendDot} style={{ background: 'var(--amber)' }} /> Medium
        <span className={styles.legendDot} style={{ background: 'var(--red)' }} /> Least
        <div style={{ flex: 1 }} />
        <span className={styles.legendMinmax}>
          max {isOcc ? fmtPct(ranked.maxP, 0) : ranked.maxP} · min {isOcc ? fmtPct(ranked.minP, 0) : ranked.minP}
        </span>
      </div>

      <div className={styles.listScroll}>
        {ranked.list.map(r => {
          const tier = heatTier(r.intensity);
          const active = selectedZone === r.zone;
          const secondary = isOcc ? `${r.totalTurns} turns` : `${r.totalPeople} people`;
          const primaryText = isOcc ? fmtPct(r.primary) : String(r.primary);
          return (
            <button
              key={r.zone}
              onClick={() => onSelect(r.zone)}
              className={`${styles.listItem} ${styles[`tier_${tier}`]} ${active ? styles.listItemActive : ''}`}
            >
              <div className={styles.listBadge}>
                <div className={styles.listBadgeLabel}>{r.label}</div>
                <div className={styles.listBadgeRank}>#{r.rank}</div>
              </div>
              <div className={styles.listItemBody}>
                <div className={styles.listItemRow}>
                  <span className={styles.listPrimary}>{primaryText}</span>
                  <span className={styles.listUnit}>{unit}</span>
                </div>
                <div className={styles.listItemMeta}>
                  <span>{secondary}</span>
                  <span className={styles.metaDim}>· stream {r.streamIndex}</span>
                </div>
                <div className={styles.listBar}>
                  <div
                    className={styles.listBarFill}
                    style={{ width: `${6 + r.intensity * 94}%` }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- HourlyChart ---------- */

function HourlyChart({ tables, hours, metric }) {
  const { labels, data } = useMemo(() => {
    const ordered = [...hours];
    const labels = ordered.map(hourBadge);
    const data = ordered.map(h => {
      if (metric === 'occupancy') {
        let sum = 0, n = 0;
        tables.forEach(t => {
          const hd = t.hourly?.[h];
          if (hd?.occupancy != null) { sum += hd.occupancy; n += 1; }
        });
        return n ? sum / n : 0;
      }
      let turns = 0;
      tables.forEach(t => { turns += t.hourly?.[h]?.turns || 0; });
      return turns;
    });
    return { labels, data };
  }, [tables, hours, metric]);

  const isOcc = metric === 'occupancy';
  const mainColor = isOcc ? '#00A3FF' : '#7c3aed';
  const fillStart = isOcc ? 'rgba(0,163,255,0.22)' : 'rgba(124,58,237,0.22)';
  const fillEnd = isOcc ? 'rgba(0,163,255,0.0)' : 'rgba(124,58,237,0.0)';

  const chartData = {
    labels,
    datasets: [{
      label: isOcc ? 'Table Occupancy %' : 'Turns',
      data,
      fill: true,
      backgroundColor: ctx => {
        const { ctx: c, chartArea } = ctx.chart;
        if (!chartArea) return fillStart;
        const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        g.addColorStop(0, fillStart);
        g.addColorStop(1, fillEnd);
        return g;
      },
      borderColor: mainColor,
      borderWidth: 2.2,
      tension: 0.38,
      pointRadius: 3.5,
      pointHoverRadius: 6,
      pointBackgroundColor: '#ffffff',
      pointBorderColor: mainColor,
      pointBorderWidth: 2,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#111827',
        titleColor: '#f9fafb',
        bodyColor: '#f9fafb',
        padding: 10,
        cornerRadius: 6,
        displayColors: false,
        titleFont: { size: 11, weight: '600' },
        bodyFont: { size: 12, weight: '700', family: "'JetBrains Mono', monospace" },
        callbacks: {
          label: ctx => isOcc ? `${Math.round(ctx.parsed.y)}% occupancy` : `${ctx.parsed.y} turns`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6b7280', font: { size: 10 }, maxRotation: 0, autoSkip: true, autoSkipPadding: 12 },
        border: { color: '#e5e7eb' },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f3f4f6' },
        ticks: {
          color: '#6b7280',
          font: { size: 10, family: "'JetBrains Mono', monospace" },
          callback: v => isOcc ? `${v}%` : v,
        },
        border: { display: false },
        ...(isOcc ? { max: 100 } : {}),
      },
    },
  };

  return (
    <div className={styles.chartBox}>
      <Line data={chartData} options={options} />
    </div>
  );
}

/* ---------- HourGallery ---------- */

function imagesForHour(hourData, metric) {
  if (!hourData) return [];
  if (metric === 'turnover') {
    return (hourData.turnDetails || [])
      .filter(td => td.image)
      .map((td, i) => ({
        uri: resolveImageUrl(td.image),
        cardTitle: `Turn #${i + 1}`,
        timeText: td.start ? `${td.start}–${td.end || ''}` : `${td.duration || 0}m`,
        meta: `${td.groupSize || 0} pax · ${td.duration || 0}m`,
      }))
      .filter(x => x.uri);
  }
  return (hourData.images || [])
    .map(resolveImageUrl)
    .filter(Boolean)
    .map((uri, i) => ({
      uri,
      cardTitle: `CAM${String(i + 1).padStart(2, '0')}`,
      timeText: '',
      meta: '',
    }));
}

function HourGallery({ table, hours, metric, label }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [activeHourIdx, setActiveHourIdx] = useState(0);
  const tabsRef = useRef(null);

  const hourBuckets = useMemo(() => {
    if (!table) return [];
    return hours.map(h => ({
      hour: h,
      label: hourBadge(h),
      images: imagesForHour(table.hourly?.[h], metric),
    }));
  }, [table, hours, metric]);

  useEffect(() => {
    if (!hourBuckets.length) return;
    const idx = hourBuckets.findIndex(h => h.images.length > 0);
    setActiveHourIdx(idx === -1 ? 0 : idx);
  }, [hourBuckets]);

  useEffect(() => {
    const el = tabsRef.current?.querySelector(`[data-h="${activeHourIdx}"]`);
    el?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [activeHourIdx]);

  const totalImages = hourBuckets.reduce((n, h) => n + h.images.length, 0);
  const emptyHours = hourBuckets.filter(h => h.images.length === 0).length;
  const active = hourBuckets[activeHourIdx];
  const activeUris = active ? active.images.map(i => i.uri) : [];

  return (
    <>
      <div className="section">
        <div className="sec-hdr">
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--g900)' }}>
            Hourly Image Gallery {label ? <span style={{ color: 'var(--blue)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>— {label}</span> : null}
          </h2>
        </div>

        {!table ? (
          <div className={styles.fdEmpty}>Pick a table from the list above to view its hourly captures</div>
        ) : (
          <>
            <div className={styles.fdTabs} ref={tabsRef}>
              {hourBuckets.map((h, i) => {
                const isActive = i === activeHourIdx;
                const isEmpty = h.images.length === 0;
                return (
                  <button
                    key={h.hour}
                    data-h={i}
                    onClick={() => setActiveHourIdx(i)}
                    className={`${styles.fdTab} ${isActive ? styles.fdTabActive : ''}`}
                    style={{ opacity: isEmpty ? 0.4 : 1 }}
                  >
                    {h.label}<span className={styles.fdTabCount}>{h.images.length}</span>
                  </button>
                );
              })}
            </div>

            {!active || active.images.length === 0 ? (
              <div className={styles.fdEmpty}>No captures in this time window</div>
            ) : (
              <div className={styles.fdGrid}>
                {active.images.map((img, i) => (
                  <div
                    key={img.uri + i}
                    className={styles.fdCard}
                    onClick={() => setLightboxIdx(i)}
                  >
                    <div className={styles.fdFrame}>
                      <img
                        src={img.uri}
                        alt={img.cardTitle}
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div className={styles.fdInfo}>
                      <div className={styles.fdZone}>{img.cardTitle}</div>
                      <div className={styles.fdRow}>
                        <div className={styles.fdTime}>{img.timeText || img.meta || '—'}</div>
                        <div className={styles.fdBadge}>#{i + 1}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {lightboxIdx !== null && activeUris.length > 0 && (
        <ImageLightbox images={activeUris} currentIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </>
  );
}

/* ---------- Main ---------- */

export default function TableMonitoring() {
  const { currentStore, exitStore, startDate } = useDashboard();
  const [metric, setMetric] = useState('occupancy');
  const [selectedZone, setSelectedZone] = useState(null);
  const [overall, setOverall] = useState(true);

  const cacheKey = currentStore ? `table-occ:${currentStore.storeId}:${startDate}` : null;
  const { data, loading } = useCache(cacheKey, () => fetchTableOccupancy(currentStore.storeId, startDate));

  const tables = (data?.tables || []).filter(t => (t.zone || '').toUpperCase() !== 'OVERALL');
  const hours = data?.hours || [];

  const { streams, labelFor } = useMemo(() => groupByStream(tables), [tables]);

  const selectedTable = useMemo(() => {
    if (!selectedZone) return null;
    return tables.find(t => (t.zone || t.name) === selectedZone) || null;
  }, [tables, selectedZone]);

  const topmostTable = useMemo(() => {
    if (!tables.length) return null;
    let best = null, bestVal = -Infinity;
    for (const t of tables) {
      let val = 0;
      if (metric === 'occupancy') {
        let sum = 0, n = 0;
        hours.forEach(h => {
          const hd = t.hourly?.[h];
          if (hd?.occupancy != null) { sum += hd.occupancy; n += 1; }
        });
        val = n ? sum / n : 0;
      } else {
        hours.forEach(h => { val += t.hourly?.[h]?.turns || 0; });
      }
      if (val > bestVal) { bestVal = val; best = t; }
    }
    return best;
  }, [tables, hours, metric]);

  if (!currentStore) return null;

  return (
    <div className="section">
      <div className="sec-hdr">
        <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--g900)' }}>Table Monitoring</h2>
      </div>

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
        <button onClick={exitStore} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 500, color: 'var(--blue)', cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'var(--font)', padding: '5px 10px', borderRadius: 7 }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8 2L4 6l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          All Stores
        </button>
        <span style={{ color: 'var(--g300)', fontSize: 11 }}>/</span>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--g900)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: currentStore.color }} />
          A2B {currentStore.name}
        </div>
      </div>

      {/* Metric toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--white)', border: '1px solid var(--g200)', borderRadius: 'var(--r)', padding: '6px 12px' }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--blue)', flexShrink: 0 }}>
            <path d="M3 3v18h18M7 16l4-4 4 4 5-6" />
          </svg>
          <select
            value={metric}
            onChange={e => { setMetric(e.target.value); setSelectedZone(null); setOverall(true); }}
            style={{ border: 'none', background: 'none', outline: 'none', fontSize: 12, fontWeight: 500, color: 'var(--g900)', fontFamily: 'var(--font)', cursor: 'pointer' }}
          >
            <option value="occupancy">Table Occupancy Rate</option>
            <option value="turnover">Table Turnover</option>
          </select>
        </div>
      </div>

      {loading ? (
        <ChartSkeleton height={300} />
      ) : tables.length === 0 ? (
        <div style={{ background: 'var(--white)', border: '1px solid var(--g200)', borderRadius: 'var(--rlg)' }}>
          <EmptyState title="No Data Available" message="No table occupancy data for the selected date" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <OverallCard tables={tables} hours={hours} metric={metric} />

          <div className={styles.splitHalves}>
            <TablesList
              streams={streams}
              hours={hours}
              metric={metric}
              selectedZone={overall ? null : selectedZone}
              onSelect={zone => { setSelectedZone(zone); setOverall(false); }}
            />

            <div className={styles.chartCard}>
              <div className={styles.cardHdr}>
                <div>
                  <div className={styles.cardTitle}>
                    {metric === 'occupancy' ? 'Table Occupancy Rate — Hourly' : 'Table Turnover — Hourly'}
                  </div>
                  <div className={styles.cardSub}>
                    {(() => {
                      if (overall) return 'Store-wide aggregate per hour';
                      const t = selectedTable || topmostTable;
                      return t ? `Selected: ${labelFor(t.zone || t.name)}` : 'No tables';
                    })()}
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--g600)' }}>Overall</span>
                  <span
                    role="switch"
                    aria-checked={overall}
                    onClick={() => {
                      setOverall(v => {
                        const next = !v;
                        if (!next && topmostTable) {
                          setSelectedZone(topmostTable.zone || topmostTable.name);
                        }
                        return next;
                      });
                    }}
                    style={{
                      width: 32, height: 18, borderRadius: 999,
                      background: overall ? 'var(--blue)' : 'var(--g300)',
                      position: 'relative', transition: 'background 0.15s',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 2, left: overall ? 16 : 2,
                      width: 14, height: 14, borderRadius: '50%', background: '#fff',
                      transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                    }} />
                  </span>
                </label>
              </div>
              <div className={styles.chartWrap}>
                <HourlyChart
                  tables={overall ? tables : (selectedTable ? [selectedTable] : (topmostTable ? [topmostTable] : []))}
                  hours={hours}
                  metric={metric}
                />
              </div>
            </div>
          </div>

          <HourGallery
            table={overall ? null : selectedTable}
            hours={hours}
            metric={metric}
            label={!overall && selectedTable ? labelFor(selectedTable.zone || selectedTable.name) : null}
          />
        </div>
      )}
    </div>
  );
}
